import { AudioSystem } from "../audio/audioSystem";
import { DecisiveView } from "./decisiveView";
import { BlackBox, PLAYER_BITS } from "./blackBox";
import { botInput, createBot, type Bot } from "../bot/bot";
import { buildRules } from "../content/rules";
import { FxSystem, type SparkPlace } from "../fx/fxSystem";
import { Devices } from "../input/devices";
import { Renderer } from "../render/renderer";
import type { OverlayWeights } from "../render/poseOverlay";
import { reactionShot, shotFor } from "../render/sprites/animation";
import { sub } from "../shared/fixed";
import {
  BUTTON,
  createMatch,
  FIGHTER,
  isSlowed,
  PHASE,
  SLOW_RATE,
  step,
  type AbilityDef,
  type FighterState,
  type InputBits,
  type MatchRules,
  type MatchState,
  type ReactionKind,
  type SimEvent,
} from "../sim";
import { PauseController } from "./pauseController";
import type { Scenario } from "./scenarios";
import { loadSettings, saveSettings, type ViewSettings } from "./settings";

export interface LocalMatchOptions {
  /** Какие места играют боты: [игрок 1, игрок 2]. */
  readonly bots: readonly [boolean, boolean];
  readonly seed: number;
  /** Проверка приёмов за Ярость живым вводом (?meter=3000): шкала у обоих не падает ниже этого. Только на одном ПК. */
  readonly meter?: number;
  /** Переиграть бой из «чёрного ящика»: ввод каждого кадра (src/app/blackBox.ts). */
  readonly replay?: readonly number[];
  /** Писать «чёрный ящик» (бой человеком на этом ПК). */
  readonly journal?: boolean;
  /** Постановочная сцена для съёмки (src/app/scenarios.ts). */
  readonly scenario?: Scenario;
  /** Съёмка: звук не играет, а пишется по шагам мира — дорожка для ролика (audio/audioSystem.ts). */
  readonly recordAudio?: boolean;
}

/**
 * Может ли кость быть передним краем удара (обмер досягаемости): стопа на полу — опора, а не удар (кроме низких),
 * колено — только у ударов ногами, у локтевых — только локоть.
 */
function canStrike(ability: AbilityDef, bone: string, up: number): boolean {
  const name = `${ability.id} ${ability.pose}`;
  if (name.includes("elbow")) return bone.endsWith("ForeArm");
  if (name.includes("knee")) return bone.endsWith("Leg");
  const kick = ability.input.button === "lightKick" || ability.input.button === "heavyKick" || name.includes("knee");
  if (bone.endsWith("Leg")) return kick;
  if (bone.endsWith("ToeBase")) return up >= FOOT_LIFT || ability.hit?.height === "low";
  return true;
}

/** Стопа выше стольких пикселей от пола — уже бьёт, ниже — опора. */
const FOOT_LIFT = 20;

/**
 * Бой на одном ПК: привязка геймпадов → бой → реванш. Связывает ввод, мир, эффекты, вибрацию и рисование;
 * правил боя здесь нет — они в мире (src/sim).
 */
export class LocalMatch {
  private readonly rules: MatchRules = buildRules(["mannequin", "mannequin"], "foundry");
  private state: MatchState;
  private readonly fx = new FxSystem();
  /** Звук боя: события мира → звуки, глухо и сердце в замедлении. */
  private readonly audio: AudioSystem;
  private readonly devices: Devices;
  private readonly renderer: Renderer;
  private readonly bots: (Bot | null)[];
  private readonly settings: ViewSettings;
  private readonly pause: PauseController;
  private pauseHeld = false;
  /** Индикатор нажатий: спрятан, F1 — показать или спрятать. */
  private showInputs = false;

  constructor(
    canvas: HTMLCanvasElement,
    target: Window,
    private readonly options: LocalMatchOptions,
  ) {
    this.page = target;
    this.audio = new AudioSystem(options.recordAudio === true);
    this.devices = new Devices(target);
    this.renderer = new Renderer(canvas);
    this.state = createMatch(this.rules, options.seed);
    this.blackBox = options.journal
      ? new BlackBox(options.seed, options.bots, options.meter ?? 0, this.kitIds(), target)
      : null;
    void this.renderer.ready.then(() => {
      this.spritesReady = true;
    });
    // бот всегда в полную силу, и против человека (правило проекта: «я не хочу чтобы бот был спокойней против человека»)
    this.bots = options.bots.map((isBot, side) =>
      isBot ? createBot(options.seed * 31 + side * 17 + 1, this.rules) : null,
    );
    const storage = (() => {
      try {
        return target.localStorage;
      } catch {
        return undefined;
      }
    })();
    this.settings = loadSettings(storage);
    target.addEventListener("keydown", (event) => {
      if (event.code !== "F1") return;
      event.preventDefault();
      this.showInputs = !this.showInputs;
    });
    this.pause = new PauseController(this.settings, (settings) => {
      saveSettings(storage, settings);
    });
  }

  private needsPlayer(slot: number): boolean {
    return !this.bots[slot] && !this.devices.slots[slot];
  }

  private assigning(): boolean {
    return this.needsPlayer(0) || this.needsPlayer(1);
  }

  tick(): void {
    this.devices.poll();
    if (this.assigning()) {
      for (const device of this.devices.confirmedThisFrame()) {
        const slot = this.devices.slots.indexOf(null);
        if (slot >= 0 && !this.bots[slot]) this.devices.claim(device);
      }
      return;
    }
    const pauseNow = this.devices.pausePressed();
    if (pauseNow && !this.pauseHeld) this.pause.toggle();
    this.pauseHeld = pauseNow;
    const confirmed = this.devices.confirmedThisFrame().length > 0;
    if (this.pause.isOpen) {
      this.pause.handle(this.devices.bits(0) | this.devices.bits(1), confirmed);
      return;
    }
    if (this.disconnected() >= 0) return;
    if (this.state.phase === PHASE.matchEnd && confirmed) {
      this.blackBox?.flush();
      this.state = createMatch(this.rules, this.state.frame);
      if (this.options.journal)
        this.blackBox = new BlackBox(
          this.state.rng,
          this.options.bots,
          this.options.meter ?? 0,
          this.kitIds(),
          this.page,
        );
      this.fx.clear();
      this.renderer.resetHud(this.state);
      return;
    }
    this.advance(this.inputs());
  }

  private lastInputs: [InputBits, InputBits] = [0, 0];
  private blackBox: BlackBox | null = null;
  /** Окно страницы: «чёрный ящик» слушает уход со страницы. */
  private readonly page: Window;
  /** Решающий удар: картинка замедления мира — ракурс, облёт, вспышка (app/decisiveView.ts). */
  private readonly decisive = new DecisiveView();

  private scenarioFrame = -1;

  private inputs(): [InputBits, InputBits] {
    const replay = this.options.replay;
    if (replay) {
      const packed = replay[this.state.frame] ?? 0;
      this.lastInputs = [packed % PLAYER_BITS, Math.floor(packed / PLAYER_BITS)];
      return this.lastInputs;
    }
    const scenario = this.options.scenario;
    if (scenario) {
      if (this.state.phase !== PHASE.fight) return [0, 0];
      if (this.scenarioFrame < 0) scenario.setup(this.state);
      this.scenarioFrame++;
      const [first, second] = scenario.input(this.scenarioFrame, this.state);
      return [first, second];
    }
    const input = (slot: 0 | 1): InputBits => {
      const bot = this.bots[slot];
      return bot ? botInput(bot, this.state, slot) : this.devices.bits(slot);
    };
    this.lastInputs = [input(0), input(1)];
    return this.lastInputs;
  }

  /** Камера сразу над бойцами (съёмка после пропуска кадров). */
  settleCamera(): void {
    this.renderer.camera.reset(this.state);
  }

  /** Один шаг мира с последствиями. Нужен и ролику: съёмка шагает по кадру. */
  advance(inputs: readonly [InputBits, InputBits]): void {
    const floor = this.options.meter ?? 0;
    if (floor > 0) for (const fighter of this.state.fighters) fighter.meter = Math.max(fighter.meter, floor);
    const events = step(this.state, this.rules, inputs);
    this.decisive.record(this.state, events);
    this.blackBox?.record(inputs, events, this.state);
    if (this.state.phase === PHASE.matchEnd && this.state.phaseFrame === 1) this.blackBox?.flush();
    this.fx.onEvents(events, this.state);
    this.audio.onStep(events, this.state);
    this.fx.update(isSlowed(this.state) ? 1 / SLOW_RATE : 1);
    this.feel(events);
  }

  /** Вибрация DualSense на попадании: сильнее тому, кого ударили. */
  private feel(events: readonly SimEvent[]): void {
    for (const event of events) {
      if (event.kind !== "hit" && event.kind !== "block") continue;
      const victim = event.who === 0 ? 1 : 0;
      const strong = event.kind === "hit" ? 0.35 + event.strength * 0.3 : 0.15;
      this.devices.rumble(victim, strong, strong * 0.6, 90 + event.strength * 60);
      this.devices.rumble(event.who, strong * 0.4, strong * 0.5, 60);
    }
  }

  private disconnected(): number {
    for (const slot of [0, 1]) if (!this.bots[slot] && !this.devices.connected(slot)) return slot;
    return -1;
  }

  private banner(): string[] {
    if (this.options.replay) return []; // переигровка «чёрного ящика»: геймпадов нет, звать игроков некого
    const usesKeyboard = this.devices.slots.some((slot) => slot?.kind === "keys");
    if (!this.devices.keyboardFocused && (this.assigning() || usesKeyboard))
      return ["КЛИКНИТЕ ПО ИГРЕ", "Окно игры не в фокусе — клавиатура пишет в другое окно"];
    if (this.assigning()) {
      const slot = this.needsPlayer(0) ? 1 : 2;
      return [
        `ИГРОК ${slot}`,
        "✕ на геймпаде или Пробел / Enter на клавиатуре",
        "Клавиатура, игрок 1: WASD — ходить, U I J K — удары, O — блок",
      ];
    }
    const lost = this.disconnected();
    if (lost >= 0) return ["ПАУЗА", `Подключите геймпад игрока ${lost + 1}`];
    return [];
  }

  render(): void {
    // сцена по записи нажатий — это игроки, а не боты (GAME-31)
    const name = (slot: 0 | 1): string =>
      this.bots[slot] && !this.options.scenario ? `БОТ ${slot + 1}` : `ИГРОК ${slot + 1}`;
    const decisive = this.decisive.next(this.state);
    this.renderer.draw(decisive?.state ?? this.state, this.rules, this.fx, {
      ...(decisive ? { decisive } : {}),
      names: [name(0), name(1)],
      banner: this.pause.isOpen || this.pause.showControls ? [] : this.banner(),
      flash: this.settings.flash / 100,
      shake: this.settings.shake / 100,
      ...(this.pause.isOpen ? { pause: this.pause.view() } : {}),
      ...(this.pause.showControls ? { controls: true } : {}),
      ...(this.options.scenario?.caption ? { caption: this.options.scenario.caption } : {}),
      ...(this.options.replay ? { caption: replayCaption(this.state, this.rules, this.lastInputs) } : {}),
      ...(this.showInputs
        ? {
            inputs: {
              players: [0, 1].map((slot) => ({
                label: `Игрок ${slot + 1}: ${this.bots[slot] ? "бот" : this.devices.label(slot)}`,
                bits: this.bots[slot] ? (this.lastInputs[slot] ?? 0) : this.devices.bits(slot),
              })),
              lastKey: this.devices.lastKey,
              pads: this.devices.padList(),
            },
          }
        : {}),
    });
  }

  /** Приёмы наборов обоих бойцов — в журнал «чёрного ящика». */
  private kitIds(): string[][] {
    return this.rules.kits.map((kit) => kit.map((ability) => ability.id));
  }

  /** Для проверки настоящими нажатиями: кто занял места, идёт ли бой, где бойцы. */
  report(): {
    assigning: boolean;
    slots: string[];
    frame: number;
    phase: number;
    paused: boolean;
    /** Решающий удар: замедление идёт / уже было в этом раунде (0 — нет, 1 — было, 2 — с нокаутом). */
    slowmo: number;
    slowmoDone: number;
    fighters: { x: number; state: number; bits: number; ability: string; health: number }[];
  } {
    return {
      assigning: this.assigning(),
      slots: [0, 1].map((slot) => (this.bots[slot] ? "бот" : this.devices.label(slot))),
      frame: this.state.frame,
      phase: this.state.phase,
      paused: this.pause.isOpen,
      slowmo: this.state.slowmo,
      slowmoDone: this.state.slowmoDone,
      fighters: this.state.fighters.map((fighter, side) => ({
        x: Math.round(fighter.x / 256),
        state: fighter.state,
        bits: fighter.lastBits,
        ability: fighter.ability >= 0 ? (this.rules.kits[side === 0 ? 0 : 1][fighter.ability]?.id ?? "") : "",
        health: fighter.health,
      })),
    };
  }

  /** Для съёмки и автоигры: шагнуть без устройств (оба места — боты). */
  stepBots(frames: number): void {
    for (let frame = 0; frame < frames; frame++) this.advance(this.inputs());
  }

  get snapshot(): MatchState {
    return this.state;
  }

  /** Атласы захвата движений загружены — для съёмки. */
  spritesReady = false;

  backgroundCanvas(): HTMLCanvasElement {
    return this.renderer.backgroundCanvas();
  }

  get artCanvas(): HTMLCanvasElement {
    return this.renderer.artCanvas;
  }

  /** Замеры для съёмки: камера и контраст бойцов к фону. */
  /** Замер позы 3D для поиска рывков (tools/capture/jerks.py). */
  /**
   * Обмер досягаемости (tools/capture/reach.mjs): для каждого удара набора — кадр клипа, который рисуется в первый
   * активный кадр, и где на нём кисти и стопы; бьющая — дальше всех вперёд.
   */
  reachTable(): { id: string; clip: string; frame: number; limb: string; forward: number; up: number }[] {
    const kit = this.rules.kits[0];
    const rows: { id: string; clip: string; frame: number; limb: string; forward: number; up: number }[] = [];
    const poseAt = (index: number, frame: number): ReturnType<Renderer["measureLimbs"]> => {
      const ability = kit[index];
      if (!ability) return null;
      const fighter = {
        ...this.state.fighters[0],
        state: FIGHTER.ability,
        ability: index,
        abilityFrame: frame,
        stateFrame: frame,
        y: ability.input.air ? -sub(60) : 0,
      };
      const shot = shotFor(fighter, ability, 0, ability.input.run === true, true);
      return this.renderer.measureLimbs(shot.clip, shot.frame);
    };
    kit.forEach((ability, index) => {
      if (!ability.hit || !ability.hitbox || ability.projectile) return;
      const impact = poseAt(index, ability.startup);
      if (!impact) return;
      // передний край удара — кость, что дальше всех вперёд в кадр удара клипа (она касается соперника первой)
      let limb = "";
      let reach = -Infinity;
      for (const [name, at] of Object.entries(impact)) {
        if (!canStrike(ability, name, at.up) || at.forward <= reach) continue;
        reach = at.forward;
        limb = name;
      }
      const place = impact[limb];
      if (!place) return;
      rows.push({ id: ability.id, clip: "", frame: 0, limb, forward: place.forward, up: place.up });
    });
    return rows;
  }

  /**
   * Обмер реакций (tools/capture/reaction.mjs): для каждого вида реакции и кадра — на сколько корпус ушёл вперёд от
   * места мира против стойки, пиксели (назад — минус). Зона попадания мира идёт за ним (sim/combat.ts hurtbox).
   */
  reactionTable(frames: number): Record<ReactionKind, number[]> {
    // удар в голову — откидывается голова, иначе — корпус; с добавками картинки (откидывание, «держит удар»)
    const torso = (clip: string, frame: number, mirror: boolean, bone: string, overlay?: OverlayWeights): number => {
      const at = this.renderer.measureLimbs(clip, frame, [bone], overlay)?.[bone];
      return at ? (mirror ? -at.forward : at.forward) : 0;
    };
    const setups: Record<ReactionKind, Partial<FighterState>> = {
      stumble: { state: FIGHTER.hitstun, stun: 30, hurtPower: 2, hurtSpot: 2, vx: 0 },
      head: { state: FIGHTER.hitstun, stun: 10, hurtPower: 1, hurtSpot: 1, vx: 0 },
      body: { state: FIGHTER.hitstun, stun: 10, hurtPower: 1, hurtSpot: 2, vx: 0 },
      air: { state: FIGHTER.airHit, fallKind: 0, y: -sub(50) },
      trip: { state: FIGHTER.airHit, fallKind: 1, y: -sub(30) },
    };
    const table = {} as Record<ReactionKind, number[]>;
    for (const kind of Object.keys(setups) as ReactionKind[]) {
      table[kind] = Array.from({ length: frames }, (_, stateFrame) => {
        // оглушение убывает по кадру, как в мире: вид реакции держится (sim/reaction.ts)
        const stun = Math.max(1, (setups[kind].stun ?? 0) - stateFrame);
        const fighter: FighterState = { ...this.state.fighters[0], facing: 1, ...setups[kind], stun, stateFrame };
        const air = kind === "air" || kind === "trip";
        const shot = air ? shotFor(fighter, undefined, 0, false, true) : reactionShot(fighter);
        const bone = kind === "head" ? "mixamorigHead" : "mixamorigSpine2";
        const overlay: OverlayWeights = {
          brace: air ? 0 : 0.5,
          flinch: Renderer.flinchOf(fighter),
          land: 0,
          recoil: 0,
        };
        return Math.round(
          torso(shot.clip, shot.frame, shot.mirror === true, bone, overlay) - torso("fighting-idle", 0, false, bone),
        );
      });
    }
    return table;
  }

  /** Обмер: где кисти, стопы, локти и колени на кадре клипа (подбор кадра удара клипа). */
  limbsAt(clip: string, frame: number): ReturnType<Renderer["measureLimbs"]> {
    return this.renderer.measureLimbs(clip, frame);
  }

  /** Звук включается жестом игрока: клавиша, клик (браузер не даёт звучать раньше). */
  unlockAudio(): void {
    this.audio.unlock();
  }

  /** Съёмка: дорожка звука от этого шага мира. */
  markAudio(): void {
    this.audio.markStart();
  }

  /** Съёмка: список прозвучавших звуков с временем. */
  audioCues(): ReturnType<AudioSystem["cueList"]> {
    return this.audio.cueList();
  }

  /** Съёмка: дорожка WAV в base64 — съёмка кладёт её на ролик. */
  async audioTrack(): Promise<string> {
    const bytes = await this.audio.renderTrack();
    let text = "";
    for (let index = 0; index < bytes.length; index += 0x8000)
      text += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
    return btoa(text);
  }

  /** Съёмка: где встали искры ударов с прошлого раза и где сейчас кисти и стопы. */
  sparks(): { placed: SparkPlace[]; limbs: ReturnType<Renderer["limbs"]> } {
    return { placed: this.fx.sparks.splice(0), limbs: this.renderer.limbs() };
  }

  probe(): ReturnType<Renderer["probe"]> {
    return this.renderer.probe();
  }

  measurements(): { camera: number; contrast: readonly [number, number]; cinematic: number } {
    this.renderer.measure = true;
    return {
      camera: this.renderer.camera.x,
      contrast: this.renderer.contrast,
      cinematic: this.state.cinematic >= 0 ? this.state.cinematicFrame : -1,
    };
  }
}

/** Кнопки кадра значками DualSense: что игрок жмёт в переигровке «чёрного ящика». */
const BUTTON_SIGNS: readonly (readonly [number, string])[] = [
  [BUTTON.left, "←"],
  [BUTTON.right, "→"],
  [BUTTON.up, "↑"],
  [BUTTON.down, "↓"],
  [BUTTON.lightPunch, "□"],
  [BUTTON.heavyPunch, "△"],
  [BUTTON.lightKick, "✕"],
  [BUTTON.heavyKick, "○"],
  [BUTTON.block, "R2"],
  [BUTTON.grab, "L1"],
  [BUTTON.super, "L2"],
  [BUTTON.taunt, "тачпад"],
];

/** Подпись кадра переигровки: у каждого игрока — кнопки и приём, который идёт. */
function replayCaption(state: MatchState, rules: MatchRules, inputs: readonly [InputBits, InputBits]): string {
  return state.fighters
    .map((fighter, side) => {
      const keys = BUTTON_SIGNS.filter(([mask]) => ((inputs[side] ?? 0) & mask) !== 0).map(([, sign]) => sign);
      const move = fighter.ability >= 0 ? (rules.kits[side === 0 ? 0 : 1][fighter.ability]?.name ?? "") : "";
      return `И${String(side + 1)}: ${keys.join(" ") || "—"}${move ? ` → ${move}` : ""}`;
    })
    .join("   ·   ");
}
